import Image from 'next/image';
import dynamic from 'next/dynamic';
import DealsPlaceholder from '@/components/v2/carousels/Deals/Placeholder';
import { WebsiteSchema } from '../homepage/WebsiteSchema';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import { getShortenCountryName } from '@/lib/use-origin-country';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const RecentlyViewed = dynamic(
  () => import('@/components/homepage/RecentlyViewed'),
  {
    ssr: false,
  },
);

const Deals = dynamic(() => import('@/components/v2/carousels/Deals'), {
  ssr: false,
  loading: () => <DealsPlaceholder />,
});

const CustomerFavorites = dynamic(
  () => import('../carousels/CustomerFavorites'),
  {
    ssr: false,
    // Rendered height of the carousel, measured per breakpoint
    loading: () => <div className="min-h-[413px] lg:min-h-[421px]" />,
  },
);

const PopularCategories = dynamic(
  () => import('@/components/commons/PopularCategories'),
  {
    ssr: true,
  },
);

const Header = ({ cartSoldOut }: { cartSoldOut: boolean }) => {
  const generateContent = () => {
    if (cartSoldOut) {
      return {
        title: t`The item(s) in your cart sold out.`,
        description: t`Don't worry, we've got more. Checkout our Best Deals & Categories below.`,
      };
    }

    return {
      title: t`Your cart is empty`,
      description: t`Checkout our Best Deals & Categories below.`,
    };
  };

  const { title, description } = generateContent();

  return (
    <div className="reebelo-container w-full py-6">
      <div className="flex items-center gap-4 sm:items-end sm:justify-center">
        <div className="h-12 shrink-0">
          <Image
            alt={t`Empty Cart`}
            src={getEdgeUrl('images/cart/empty_cart.png')}
            className="h-full w-auto"
            width={100}
            height={100}
          />
        </div>
        <div>
          <h1 className=" text-base font-bold leading-none">{title}</h1>
          <p className="mt-1 text-sm leading-tight sm:mt-0 sm:text-base">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function EmptyCart({ cartSoldOut }: { cartSoldOut: boolean }) {
  return (
    <>
      <WebsiteSchema
        title={t`Your Shopping Cart - Reebelo ${{
          country: getShortenCountryName(),
        }}`}
        description={t`Review your cart, update quantities, and proceed to secure checkout on Reebelo. Get the best deals on refurbished tech with warranty and fast shipping.`}
      />
      <Header cartSoldOut={cartSoldOut} />
      <div className="bg-white">
        <div className="reebelo-container w-full">
          <RecentlyViewed />
        </div>
        <Deals />
      </div>
      <CustomerFavorites />
      <div className="bg-white">
        <div className="reebelo-container w-full">
          <PopularCategories />
        </div>
      </div>
    </>
  );
}
